import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from "react"
import type { User, Session } from "@supabase/supabase-js"
import posthog from 'posthog-js'
import {
  supabase,
  signIn as supabaseSignIn,
  signUp as supabaseSignUp,
  signOut as supabaseSignOut,
  getWizardSubmissionForUser,
  type WizardSubmissionRow,
  type AuthResult,
} from "@/lib/supabase"
import { clearPending } from "@/lib/passwordRecovery"

// ============================================
// TIMEOUT HELPER
// ============================================

/**
 * Enveloppe une promesse avec un timeout. Si elle ne résout pas à temps,
 * rejette avec un message d'erreur clair.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeoutPromise = new Promise<T>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label} — délai dépassé (${ms / 1000}s).`)),
      ms
    )
  })
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer)
  })
}

const HYDRATION_TIMEOUT = 8000
const SIGNOUT_TIMEOUT = 8000

/**
 * Même contenu ? Supabase renvoie un NOUVEL objet à chaque événement, même quand rien
 * n'a changé. Les effets qui en dépendent (SessionPage recharge sa séance quand `user`
 * ou `wizardData` change) se relançaient donc à chaque retour sur l'onglet.
 */
function sameContent<T>(a: T | null, b: T | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

// ============================================
// TYPES
// ============================================

interface AuthContextType {
  user: User | null
  session: Session | null
  wizardData: WizardSubmissionRow | null
  loading: boolean
  wizardLoading: boolean
  isAdmin: boolean
  adminLoading: boolean
  signIn: (email: string, password: string) => Promise<AuthResult>
  signUp: (email: string, password: string, firstName?: string) => Promise<AuthResult>
  /** `local` : cet appareil seulement (défaut : tous les appareils du compte). */
  signOut: (scope?: "global" | "local") => Promise<void>
  refreshWizardData: () => Promise<void>
  hasWizard: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider")
  return ctx
}

// ============================================
// PROVIDER
// ============================================

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [wizardData, setWizardData] = useState<WizardSubmissionRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [wizardLoading, setWizardLoading] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminLoading, setAdminLoading] = useState(false)

  /** Compte dont le diagnostic est déjà chargé — null tant qu'aucun ne l'est. */
  const loadedUserIdRef = useRef<string | null>(null)

  /**
   * `background` : relecture d'un compte DÉJÀ chargé (revérification de session au retour
   * sur l'onglet, jeton rafraîchi). Elle ne passe pas `wizardLoading` à true : l'app
   * affiche l'écran de chargement à la place de la page tant que ce drapeau est levé, ce
   * qui DÉMONTAIT la page en cours à chaque retour sur l'onglet — diagnostic renvoyé à la
   * question des blessures, séance en cours rechargée.
   */
  const fetchWizardData = useCallback(async (userId: string, background = false) => {
    if (!background) setWizardLoading(true)
    try {
      const data = await getWizardSubmissionForUser(userId)
      if (background) {
        // `null` peut venir d'une erreur réseau (la lecture ne lève pas) : une relecture
        // en arrière-plan ne doit pas faire croire que le diagnostic a disparu, ce qui
        // renverrait un client existant vers le diagnostic. Contenu inchangé : on garde
        // le même objet, pour ne relancer aucun effet.
        if (data) setWizardData((prev) => (sameContent(prev, data) ? prev : data))
      } else {
        setWizardData(data)
      }
      loadedUserIdRef.current = userId
    } catch (err) {
      console.error("[Auth] fetchWizardData failed:", err)
      if (!background) setWizardData(null)
    } finally {
      if (!background) setWizardLoading(false)
    }
  }, [])

  // Check admin via RPC public.is_admin() (créée en S10).
  // Non bloquant pour l'app : par défaut isAdmin=false, on update si la RPC confirme.
  const fetchAdminStatus = useCallback(async () => {
    setAdminLoading(true)
    try {
      const { data, error } = await supabase.rpc("is_admin")
      if (error) {
        console.error("[Auth] is_admin RPC failed:", error)
        setIsAdmin(false)
      } else {
        setIsAdmin(data === true)
      }
    } catch (err) {
      console.error("[Auth] fetchAdminStatus failed:", err)
      setIsAdmin(false)
    } finally {
      setAdminLoading(false)
    }
  }, [])

  const refreshWizardData = useCallback(async () => {
    const { data: u } = await supabase.auth.getUser()
    if (!u.user?.id) return
    await fetchWizardData(u.user.id)
  }, [fetchWizardData])

  useEffect(() => {
    let mounted = true

    // Hydratation : si Supabase hang, le loading screen resterait pour toujours.
    // On wrap avec un timeout : après 8s, on abandonne et on affiche l'app
    // en mode "non connecté". L'utilisateur verra la page de login.
    const hydrate = async () => {
      try {
        const { data: { session: s } } = await withTimeout(
          supabase.auth.getSession(),
          HYDRATION_TIMEOUT,
          "Récupération de session"
        )
        if (!mounted) return
        setSession(s)
        setUser(s?.user ?? null)

        if (s?.user) {
          try {
            const d = await withTimeout(
              getWizardSubmissionForUser(s.user.id),
              HYDRATION_TIMEOUT,
              "Récupération du profil"
            )
            if (mounted) {
              setWizardData(d)
              loadedUserIdRef.current = s.user.id
            }
          } catch (err) {
            console.error("[Auth] hydration wizard fetch failed:", err)
            // Pas bloquant : on laisse wizardData à null, le routeur renverra vers le wizard.
          }
          // Admin status — non bloquant pour l'hydratation, fire-and-forget.
          // L'app peut afficher /dashboard pendant que le check admin tourne.
          fetchAdminStatus().catch((err) => {
            console.error("[Auth] hydration admin check failed:", err)
          })
        }
      } catch (err) {
        console.error("[Auth] hydration failed (timeout or error):", err)
        // En cas de timeout, on repart à zéro : pas de session.
        // L'utilisateur devra se reconnecter, c'est préférable au blocage.
      } finally {
        if (mounted) setLoading(false)
      }
    }

    hydrate()

    // Listener auth state. Callback NON-async + setTimeout pour appels Supabase.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, s) => {
        if (!mounted) return
        console.log("[Auth] event:", event)

        setSession(s)
        // Même compte, même contenu : on garde l'objet existant (voir `sameContent`).
        const nextUser = s?.user ?? null
        setUser((prev) => (sameContent(prev, nextUser) ? prev : nextUser))

        if (event === "SIGNED_OUT") {
          loadedUserIdRef.current = null
          // Une récupération de mot de passe laissée en plan ne doit pas ressortir à la
          // prochaine connexion dans cet onglet.
          clearPending()
          setWizardData(null)
          setIsAdmin(false)
          posthog.reset()
          return
        }

        if ((event === "INITIAL_SESSION" || event === "SIGNED_IN") && s?.user) {
          posthog.identify(s.user.id, {
            email: s.user.email,
            signup_date: s.user.created_at,
          })
        }

        if ((event === "SIGNED_IN" || event === "USER_UPDATED" || event === "TOKEN_REFRESHED") && s?.user) {
          const uid = s.user.id
          // Supabase émet SIGNED_IN à CHAQUE retour sur l'onglet (auth-js, `_recoverAndRefresh`),
          // même sans rien changer. L'écran de chargement n'est justifié que pour un vrai
          // changement de compte (connexion depuis /auth) : c'est lui qui évite d'afficher
          // brièvement le diagnostic à un client qui en a déjà un.
          const background = loadedUserIdRef.current === uid
          setTimeout(() => {
            if (!mounted) return
            fetchWizardData(uid, background).catch((err) => {
              console.error("[Auth] deferred wizard fetch failed:", err)
            })
          }, 0)
        }

        // Admin status : uniquement à un vrai sign-in.
        // TOKEN_REFRESHED (toutes les ~60min) et USER_UPDATED (email/password)
        // ne changent pas le statut admin → pas la peine de retaper la RPC.
        if (event === "SIGNED_IN" && s?.user) {
          setTimeout(() => {
            if (!mounted) return
            fetchAdminStatus().catch((err) => {
              console.error("[Auth] deferred admin check failed:", err)
            })
          }, 0)
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [fetchWizardData, fetchAdminStatus])

  const signIn = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      return await supabaseSignIn(email, password)
    },
    []
  )

  const signUp = useCallback(
    async (email: string, password: string, firstName?: string): Promise<AuthResult> => {
      return await supabaseSignUp(email, password, firstName)
    },
    []
  )

  /**
   * SignOut avec timeout : si l'appel hang, on force le reset local après 8s.
   * L'utilisateur est déconnecté côté client même si le serveur n'a pas répondu.
   */
  const signOut = useCallback(async (scope: "global" | "local" = "global"): Promise<void> => {
    try {
      await withTimeout(supabaseSignOut(scope), SIGNOUT_TIMEOUT, "Déconnexion")
    } catch (err) {
      console.error("[Auth] signOut timeout/error:", err)
      // On continue quand même — le user veut se déconnecter, on l'obéit côté client
    }
    // reset() appelé ici aussi en cas de timeout (SIGNED_OUT n'aurait pas fire)
    posthog.reset()
    loadedUserIdRef.current = null
    clearPending()
    setUser(null)
    setSession(null)
    setWizardData(null)
    setIsAdmin(false)
    // Nettoyage local de secours
    try {
      localStorage.removeItem("sb-hzuvklijyvnpginpeiwy-auth-token")
    } catch {
      // ignore
    }
  }, [])

  const value: AuthContextType = {
    user,
    session,
    wizardData,
    loading,
    wizardLoading,
    isAdmin,
    adminLoading,
    signIn,
    signUp,
    signOut,
    refreshWizardData,
    hasWizard: !!wizardData,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ============================================
// LOADING SCREEN
// ============================================

export function AuthLoadingScreen() {
  return (
    <div className="min-h-screen bg-[#060a13] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
        <p className="text-[13px] text-gray-500">Chargement…</p>
      </div>
    </div>
  )
}
